import { Action } from '../actions';
import { SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR } from '../actions/campaignsEdit';
import {
    ON_ACCEPT_MINUS_PHRASE_GROUPS_MASS_ACTIONS,
    OPEN_MINUS_PHRASE_GROUPS_MASS_ACTIONS,
    OpenMinusPhraseGroupsMassActionsAction
} from '../actions/groups-edit/groups-edit-mass-actions';
import { SAVE_GROUP_MINUS_PHRASES_EDITOR } from '../actions/groupsEdit';
import {
    ACCEPT_LIBRARY_LIST_EDITOR_DATA,
    AcceptLibraryListEditorDataAction,
    CHANGE_LIBRARY_LIST_EDITOR_DATA,
    CHANGE_MINUS_PHRASES_EDITOR,
    ChangeLibraryListEditorDataAction,
    ChangeMinusPhrasesEditor,
    CLEAR_ERRORS_MINUS_PHRASES_EDITOR,
    ClearErrorsMinusPhrasesEditor,
    CLOSE_MINUS_PHRASES_EDITOR,
    CLOSE_MODAL_ADD_LIBRARY_LIST,
    CloseMinusPhrasesEditor,
    CloseModalAddLibraryListAction,
    FETCH_LIBRARY_LISTS_FOR_EDIT,
    FetchLibraryListsForEditAction,
    GET_LIBRARY_LISTS,
    GET_LIBRARY_LISTS_ERROR,
    GetLibraryListsAction,
    GetLibraryListsErrorAction,
    OPEN_MINUS_PHRASES_EDITOR,
    OPEN_MODAL_EDIT_ADD_LIBRARY_LIST,
    OpenMinusPhrasesEditor,
    OpenModalEditAddLibraryListAction,
    SET_ADDED_LIBRARY_LIST,
    SetAddedLibraryListAction,
    SHOW_EDIT_PAGE_MINUS_PHRASES_EDITOR_ERRORS,
    SHOW_ERROR_LIBRARY_LIST_EDITOR,
    ShowEditPageMinusPhrasesEditorErrors,
    ShowErrorLibraryListEditorAction,
    TOGGLE_EDIT_LIBRARY_LIST,
    ToggleEditLibraryListAction
} from '../actions/minusPhrasesEditor';
import { MassActionId } from '../typings/groupsEditState';
import { MinusPhrasesLibraryItem } from '../typings/minusPhraseLibrary';
import { MinusPhraseLibraryEditorState, State } from '../typings/state';

const initMinusKeywordsData = (
    state: MinusPhraseLibraryEditorState,
    action: OpenMinusPhrasesEditor | OpenMinusPhraseGroupsMassActionsAction
): MinusPhraseLibraryEditorState => {
    const { editorId, minusPhrases, selectedLibraryList } = action;

    const errors =
        (state.data &&
            state.data[editorId] &&
            state.data[editorId].minusKeywordsEditor.errors) ||
        [];

    return {
        ...state,
        data: {
            ...state.data,
            [editorId]: {
                minusKeywordsEditor: {
                    value: minusPhrases,
                    selectedLibraryListItems: (selectedLibraryList || []).map(
                        item => item.id
                    ),
                    isOpen: true,
                    errors
                }
            }
        }
    };
};

const changeMinusKeywordsEditorValue = (
    state: MinusPhraseLibraryEditorState,
    action: ChangeMinusPhrasesEditor
): MinusPhraseLibraryEditorState => {
    const { editorId, minusKeywords } = action;

    return {
        ...state,
        data: {
            ...state.data,
            [editorId]: {
                ...state.data[editorId],
                minusKeywordsEditor: {
                    ...state.data[editorId].minusKeywordsEditor,
                    value: minusKeywords
                }
            }
        }
    };
};

const updateLibraryLists = (
    state: MinusPhraseLibraryEditorState,
    action: GetLibraryListsAction
): MinusPhraseLibraryEditorState => {
    const data = state.data[action.editorId];
    const { selectedLibraryListItems = [] } = data.minusKeywordsEditor;
    const libraryItems = action.data;

    if (selectedLibraryListItems.length === 0) {
        return {
            ...state,
            libraryItems,
            libraryItemsApiError: false,
            libraryItemsIsLoading: false,
            isLibraryListsFetched: true
        };
    }

    const newSelectedLibraryListItems = selectedLibraryListItems.filter(
        (id: string) => {
            return Boolean(
                libraryItems.find(
                    (item: MinusPhrasesLibraryItem) => item.id === id
                )
            );
        }
    );

    return {
        ...state,
        data: {
            ...state.data,
            [action.editorId]: {
                ...data,
                minusKeywordsEditor: {
                    ...data.minusKeywordsEditor,
                    selectedLibraryListItems: newSelectedLibraryListItems
                }
            }
        },
        libraryItems,
        libraryItemsApiError: false,
        libraryItemsIsLoading: false,
        isLibraryListsFetched: true
    };
};

const showLibraryListsError = (
    state: MinusPhraseLibraryEditorState,
    action: GetLibraryListsErrorAction
): MinusPhraseLibraryEditorState => {
    return {
        ...state,
        libraryItemsApiError: true
    };
};

const fetchLibraryLists = (
    state: MinusPhraseLibraryEditorState,
    action: FetchLibraryListsForEditAction
): MinusPhraseLibraryEditorState => {
    return {
        ...state,
        libraryItemsApiError: false,
        libraryItemsIsLoading: true
    };
};

const toggleLibraryList = (
    state: MinusPhraseLibraryEditorState,
    action: ToggleEditLibraryListAction
): MinusPhraseLibraryEditorState => {
    const { editorId, itemId } = action;
    const data = state.data[editorId];

    const selectedLibraryListItems =
        data.minusKeywordsEditor.selectedLibraryListItems || [];
    let newSelectedLibraryListItems: string[] = [];

    if (selectedLibraryListItems.includes(itemId)) {
        newSelectedLibraryListItems = selectedLibraryListItems.filter(
            item => item !== itemId
        );
    } else {
        newSelectedLibraryListItems = [...selectedLibraryListItems, itemId];
    }

    const errors =
        (state.data[editorId] &&
            state.data[editorId].minusKeywordsEditor.errors) ||
        [];

    return {
        ...state,
        data: {
            ...state.data,
            [editorId]: {
                ...data,
                minusKeywordsEditor: {
                    ...data.minusKeywordsEditor,
                    selectedLibraryListItems: newSelectedLibraryListItems,
                    errors
                },
                addLibraryListEditor: data.addLibraryListEditor
                    ? {
                          ...data.addLibraryListEditor,
                          errors: []
                      }
                    : undefined
            }
        }
    };
};

const openModalAddLibraryList = (
    state: MinusPhraseLibraryEditorState,
    action: OpenModalEditAddLibraryListAction
): MinusPhraseLibraryEditorState => {
    const { editorId } = action;
    const data = state.data[editorId];

    return {
        ...state,
        data: {
            ...state.data,
            [editorId]: {
                ...data,
                addLibraryListEditor: {
                    listTitle: '',
                    minusPhrases: '',
                    isVisible: true,
                    isLoading: false,
                    errors: []
                }
            }
        }
    };
};

const closeModalAddLibraryList = (
    state: MinusPhraseLibraryEditorState,
    action: CloseModalAddLibraryListAction
): MinusPhraseLibraryEditorState => {
    const data = state.data[action.editorId];

    return {
        ...state,
        data: {
            ...state.data,
            [action.editorId]: {
                ...data,
                addLibraryListEditor: {
                    listTitle: '',
                    minusPhrases: '',
                    isVisible: false,
                    isLoading: false,
                    errors: []
                }
            }
        }
    };
};

const closeMinusKeywordsEditor = (
    state: MinusPhraseLibraryEditorState,
    action: CloseMinusPhrasesEditor
): MinusPhraseLibraryEditorState => {
    const { editorId } = action;

    const minusPhrasesEditor = state.data[editorId];
    const errors =
        (state.data[editorId] &&
            state.data[editorId].minusKeywordsEditor.errors) ||
        [];

    return {
        ...state,
        data: {
            ...state.data,
            [editorId]: {
                ...minusPhrasesEditor,
                minusKeywordsEditor: {
                    value: [],
                    selectedLibraryListItems: [],
                    isOpen: false,
                    errors
                }
            }
        }
    };
};

const changeDataLibraryListEditor = (
    state: MinusPhraseLibraryEditorState,
    action: ChangeLibraryListEditorDataAction
): MinusPhraseLibraryEditorState => {
    const data = state.data[action.editorId];

    if (data.addLibraryListEditor) {
        return {
            ...state,
            data: {
                ...state.data,
                [action.editorId]: {
                    ...data,
                    addLibraryListEditor: {
                        ...data.addLibraryListEditor,
                        listTitle: action.title,
                        minusPhrases: action.minusPhrases
                    }
                }
            }
        };
    } else {
        return state;
    }
};

const acceptLibraryListEditorData = (
    state: MinusPhraseLibraryEditorState,
    action: AcceptLibraryListEditorDataAction
): MinusPhraseLibraryEditorState => {
    const { editorId } = action;
    const data = state.data[editorId];

    if (data.addLibraryListEditor) {
        return {
            ...state,
            data: {
                ...state.data,
                [editorId]: {
                    ...data,
                    addLibraryListEditor: {
                        ...data.addLibraryListEditor,
                        isLoading: true,
                        errors: []
                    }
                }
            }
        };
    } else {
        return state;
    }
};

const showErrorLibraryListEditor = (
    state: MinusPhraseLibraryEditorState,
    action: ShowErrorLibraryListEditorAction
): MinusPhraseLibraryEditorState => {
    const { editorId } = action;
    const data = state.data[editorId];

    if (data.addLibraryListEditor) {
        return {
            ...state,
            data: {
                ...state.data,
                [editorId]: {
                    ...data,
                    addLibraryListEditor: {
                        ...data.addLibraryListEditor,
                        isLoading: false,
                        errors: action.errors
                    }
                }
            }
        };
    } else {
        return state;
    }
};

const setAddedLibraryList = (
    state: MinusPhraseLibraryEditorState,
    action: SetAddedLibraryListAction
): MinusPhraseLibraryEditorState => {
    const data = state.data[action.editorId];
    const selectedLibraryListItems =
        data.minusKeywordsEditor.selectedLibraryListItems || [];

    if (selectedLibraryListItems.length < action.maxLength) {
        selectedLibraryListItems.push(action.addedLibraryList);
    }

    return {
        ...state,
        data: {
            ...state.data,
            [action.editorId]: {
                ...data,
                minusKeywordsEditor: {
                    ...data.minusKeywordsEditor,
                    selectedLibraryListItems: selectedLibraryListItems,
                    addedLibraryList: action.addedLibraryList
                }
            }
        }
    };
};

export const initialMinusPhraseEditorState = {
    minusKeywordsEditor: {
        value: [],
        errors: [],
        isOpen: false
    }
};

export const initialMinusPhraseLibraryEditorState: MinusPhraseLibraryEditorState = {
    data: {
        [MassActionId.groupsEdit]: {
            minusKeywordsEditor: {
                ...initialMinusPhraseEditorState.minusKeywordsEditor,
                isOpen: true
            }
        }
    }
};

const clearErrorsEditorMinusKeywords = (
    state: MinusPhraseLibraryEditorState,
    action: ClearErrorsMinusPhrasesEditor
): MinusPhraseLibraryEditorState => {
    const { editorId } = action;

    return {
        ...state,
        data: {
            ...state.data,
            [editorId]: {
                ...state.data[editorId],
                minusKeywordsEditor: {
                    ...state.data[editorId].minusKeywordsEditor,
                    errors: []
                }
            }
        }
    };
};

const showEditPageMinusPhrasesEditorErrors = (
    state: MinusPhraseLibraryEditorState,
    action: ShowEditPageMinusPhrasesEditorErrors
): MinusPhraseLibraryEditorState => {
    const {
        editorId,
        minusKeywordsErrors,
        libraryMinusKeywordsIdsErrors
    } = action;
    const { addLibraryListEditor } = state.data[editorId];

    return {
        ...state,
        data: {
            ...state.data,
            [editorId]: {
                ...state.data[editorId],
                minusKeywordsEditor: {
                    ...state.data[editorId].minusKeywordsEditor,
                    errors: minusKeywordsErrors
                },
                addLibraryListEditor: addLibraryListEditor
                    ? {
                          ...addLibraryListEditor,
                          errors: libraryMinusKeywordsIdsErrors
                      }
                    : undefined
            }
        }
    };
};

export function minusPhraseLibraryEditor(
    state: MinusPhraseLibraryEditorState = initialMinusPhraseLibraryEditorState,
    action: Action,
    fullState: State
): MinusPhraseLibraryEditorState {
    switch (action.type) {
        case CLEAR_ERRORS_MINUS_PHRASES_EDITOR:
            return clearErrorsEditorMinusKeywords(state, action);

        case CHANGE_MINUS_PHRASES_EDITOR:
            return changeMinusKeywordsEditorValue(state, action);

        case SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR:
        case SAVE_GROUP_MINUS_PHRASES_EDITOR:
        case ON_ACCEPT_MINUS_PHRASE_GROUPS_MASS_ACTIONS:
            return {
                ...state,
                libraryItemsIsUpdating: true
            };

        case GET_LIBRARY_LISTS:
            return updateLibraryLists(state, action);

        case GET_LIBRARY_LISTS_ERROR:
            return showLibraryListsError(state, action);

        case TOGGLE_EDIT_LIBRARY_LIST:
            return toggleLibraryList(state, action);

        case OPEN_MODAL_EDIT_ADD_LIBRARY_LIST:
            return openModalAddLibraryList(state, action);

        case CLOSE_MODAL_ADD_LIBRARY_LIST:
            return closeModalAddLibraryList(state, action);

        case CHANGE_LIBRARY_LIST_EDITOR_DATA:
            return changeDataLibraryListEditor(state, action);

        case ACCEPT_LIBRARY_LIST_EDITOR_DATA:
            return acceptLibraryListEditorData(state, action);

        case SHOW_ERROR_LIBRARY_LIST_EDITOR:
            return showErrorLibraryListEditor(state, action);

        case FETCH_LIBRARY_LISTS_FOR_EDIT:
            return fetchLibraryLists(state, action);

        case SET_ADDED_LIBRARY_LIST:
            return setAddedLibraryList(state, action);

        case OPEN_MINUS_PHRASE_GROUPS_MASS_ACTIONS:
        case OPEN_MINUS_PHRASES_EDITOR: {
            return initMinusKeywordsData(state, action);
        }

        case CLOSE_MINUS_PHRASES_EDITOR: {
            return closeMinusKeywordsEditor(state, action);
        }

        case SHOW_EDIT_PAGE_MINUS_PHRASES_EDITOR_ERRORS: {
            return showEditPageMinusPhrasesEditorErrors(state, action);
        }

        default:
            return state;
    }
}
